import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import {
  Briefcase, Users, FileText, LogOut, Loader2, ChevronRight, RefreshCw, UserPlus, Send, Power, PowerOff,
  MapPin, CalendarDays, Clock, Inbox, ExternalLink, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import PortalBrandPanel, { DCS_LOGO_URL } from '../../components/portal/PortalBrandPanel';
import { portalFetch, getPortalUser, clearPortalSession, PORTAL_OFF_MESSAGE } from '../../lib/clientPortalApi';

// DCS Client Portal — home (Track A / A6, 6 Sep 2026; restyled portal-brand-copy-2026-09-06).
// Served at /portal/home (main's exact /portal route redirects to the candidate portal). One page,
// three tabs: Requirements (with candidates per requirement) | Agreements | Team (owner).
// Every candidate field shown here came through the server whitelist (candidate_view): no phone / email /
// CTC / employer unless the requirement's share settings allow it.
//
// The restyle is presentation only. Every call, path and field name below is byte-for-byte the
// Track A original: /me, /requirements, /requirements/{id}/candidates, /agreements, /team,
// /team/invite, /team/{id}/resend-invite, /team/{id}/disable.

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sign-in route. Branch portal-login-redesign-2026-09-06 moves /portal/login -> /portal/client.
// If that branch drops the /portal/login redirect in App.js, change this ONE line to '/portal/client'.
const LOGIN_PATH = '/portal/client';

const GOLD = '#C9A54E';
const GOLD_DEEP = '#A8863A';
const HAIRLINE = '#E5E1D5';
const CREAM = '#FDFCF9';

const STAGE_LABEL = {
  shortlisted: 'Shortlisted', lined_up: 'Interview lined up', interviewed: 'Interviewed',
  offered: 'Offered', joined: 'Joined', rejected: 'Not selected'
};
const STAGE_STYLE = {
  shortlisted: 'bg-blue-50 text-blue-700 border-blue-200',
  lined_up: 'bg-violet-50 text-violet-700 border-violet-200',
  interviewed: 'bg-amber-50 text-amber-800 border-amber-200',
  offered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  joined: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-slate-100 text-slate-600 border-slate-200'
};
const AGREEMENT_STYLE = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  generated: 'bg-slate-100 text-slate-700 border-slate-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  opened: 'bg-amber-50 text-amber-800 border-amber-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200'
};
const AGREEMENT_LABEL = {
  draft: 'Draft', generated: 'Ready to send', sent: 'Sent to you',
  opened: 'Opened', active: 'Signed', archived: 'Archived'
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const pill = 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap';

export default function ClientPortalHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [team, setTeam] = useState({ users: [], is_owner: false });
  const [openReq, setOpenReq] = useState(null);
  const [candidates, setCandidates] = useState({});
  const [candBusy, setCandBusy] = useState(null);

  const logout = useCallback(() => {
    clearPortalSession();
    navigate(LOGIN_PATH, { replace: true });
  }, [navigate]);

  const handleErr = useCallback((err) => {
    if (err && (err.status === 401)) { logout(); return; }
    if (err && err.status === 503) { setError(PORTAL_OFF_MESSAGE); return; }
    toast.error((err && err.detail) || 'Something went wrong');
  }, [logout]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const m = await portalFetch('/me');
      setMe(m);
      if (m.must_change_password) { navigate(LOGIN_PATH, { replace: true }); return; }
      const [r, a, t] = await Promise.all([
        portalFetch('/requirements'),
        portalFetch('/agreements'),
        portalFetch('/team')
      ]);
      setRequirements((r && r.requirements) || []);
      setAgreements((a && a.agreements) || []);
      setTeam((t && Array.isArray(t.users)) ? t : { users: [], is_owner: false });
    } catch (err) {
      handleErr(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [handleErr, navigate]);

  useEffect(() => {
    if (!getPortalUser()) { navigate(LOGIN_PATH, { replace: true }); return; }
    load();
  }, [load, navigate]);

  const refresh = () => { setRefreshing(true); load(); };

  const toggleReq = async (req) => {
    if (openReq === req.id) { setOpenReq(null); return; }
    setOpenReq(req.id);
    if (candidates[req.id]) return;
    setCandBusy(req.id);
    try {
      const data = await portalFetch(`/requirements/${req.id}/candidates`);
      setCandidates((c) => ({ ...c, [req.id]: data }));
    } catch (err) {
      handleErr(err);
    } finally {
      setCandBusy(null);
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: CREAM }} data-testid="cp-loading">
        <div className="flex flex-col items-center gap-3">
          <img src={DCS_LOGO_URL} alt="" onError={hideOnError} className="w-10 h-10 object-contain rounded opacity-80" />
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: GOLD_DEEP }} />
          <p className="text-xs text-slate-500">Opening your portal…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: CREAM }} data-testid="cp-portal-off">
        <Card className="max-w-md w-full" style={{ borderColor: HAIRLINE }}>
          <CardContent className="p-6 text-center">
            <img src={DCS_LOGO_URL} alt="" onError={hideOnError} className="w-10 h-10 object-contain rounded mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-slate-900">The portal is closed just now</h1>
            <p className="text-sm text-slate-600 mt-2">{error}. Nothing is lost — your roles and papers are all still here.</p>
            <p className="text-sm text-slate-600 mt-2">
              Write to{' '}
              <a href="mailto:care@daruwallas.com" className="font-medium hover:underline" style={{ color: GOLD_DEEP }}>care@daruwallas.com</a>
              {' '}and we will tell you when it is back.
            </p>
            <Button variant="outline" className="mt-5" onClick={logout} data-testid="cp-off-back">Back to sign in</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const openCount = requirements.filter((r) => r.is_open).length;
  const sharedCount = requirements.reduce((n, r) => n + (r.candidates_shared || 0), 0);
  const liveAgreements = agreements.filter((a) => !a.is_archived).length;
  const clientName = (me && me.client && me.client.name) || 'Your company';
  const userName = (me && me.user && me.user.full_name) || '';

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }} data-testid="client-portal-home">
      <header className="bg-white sticky top-0 z-20" data-testid="cp-header">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3">
          <img
            src={DCS_LOGO_URL}
            alt="Daruwalla Consultancy Services"
            onError={hideOnError}
            className="w-9 h-9 sm:w-10 sm:h-10 object-contain rounded shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] sm:text-[11px] uppercase font-bold tracking-[0.28em]" style={{ color: GOLD_DEEP }}>
              DCS Client Portal
            </div>
            <h1 className="text-base sm:text-xl font-semibold text-slate-900 truncate leading-tight" data-testid="cp-client-name">
              {clientName}
            </h1>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-sm text-slate-600 shrink-0">
            <span className="max-w-[12rem] truncate" data-testid="cp-user-name">{userName}</span>
            <Badge variant="outline" className="capitalize">{me?.portal_role}</Badge>
            <Button variant="ghost" size="icon" onClick={refresh} title="Refresh" data-testid="cp-refresh">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="cp-logout">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
          <div className="flex sm:hidden items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={refresh} title="Refresh" data-testid="cp-refresh-mobile">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} title="Sign out" data-testid="cp-logout-mobile">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="sm:hidden max-w-5xl mx-auto px-4 pb-2 flex items-center gap-2 text-xs text-slate-500">
          <span className="truncate" data-testid="cp-user-name-mobile">{userName}</span>
          <Badge variant="outline" className="capitalize text-[10px] px-2 py-0">{me?.portal_role}</Badge>
        </div>
        <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DEEP} 55%, ${GOLD} 100%)` }} />
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5 sm:py-6 space-y-5">
        <PortalBrandPanel variant="home" clientName={clientName} showLogo={false} />

        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Kpi icon={Briefcase} label="Open roles" value={openCount} testid="cp-kpi-open-requirements" />
          <Kpi icon={Users} label="Profiles shared" value={sharedCount} testid="cp-kpi-candidates-shared" />
          <Kpi icon={FileText} label="Agreements" value={liveAgreements} testid="cp-kpi-agreements" />
        </div>

        <Tabs defaultValue="requirements" data-testid="cp-tabs">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1">
            <TabsTrigger value="requirements" className="py-2 text-xs sm:text-sm" data-testid="cp-tab-requirements">Roles</TabsTrigger>
            <TabsTrigger value="agreements" className="py-2 text-xs sm:text-sm" data-testid="cp-tab-agreements">Agreements</TabsTrigger>
            <TabsTrigger value="team" className="py-2 text-xs sm:text-sm" data-testid="cp-tab-team">Your team</TabsTrigger>
          </TabsList>

          <TabsContent value="requirements" className="mt-4">
            {requirements.length === 0 ? (
              <Empty
                icon={Inbox}
                title="No roles opened yet"
                body="Send the job description to care@daruwallas.com and your recruiter will open the role for you. It shows up on this page the same day, and every profile we shortlist lands under it."
                testid="cp-empty-requirements"
              />
            ) : (
              <div className="space-y-2.5">
                {requirements.map((r) => (
                  <Card key={r.id} className="overflow-hidden" style={{ borderColor: HAIRLINE }}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        className="w-full text-left px-3 sm:px-4 py-3 flex items-start gap-2.5 hover:bg-slate-50/70 transition-colors"
                        onClick={() => toggleReq(r)}
                        aria-expanded={openReq === r.id}
                        data-testid={`cp-req-${r.id}`}
                      >
                        <ChevronRight
                          className={`w-4 h-4 text-slate-400 mt-0.5 shrink-0 transition-transform ${openReq === r.id ? 'rotate-90' : ''}`}
                          aria-hidden="true"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-slate-900 leading-snug break-words">{r.position_title || 'Untitled role'}</span>
                            <span
                              className={`${pill} ml-auto shrink-0 ${r.is_open ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}
                            >
                              {r.status || (r.is_open ? 'open' : 'closed')}
                            </span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                            {r.headcount ? (
                              <span className="inline-flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" aria-hidden="true" />
                                {r.headcount} opening{r.headcount > 1 ? 's' : ''}
                              </span>
                            ) : null}
                            {r.location ? (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" aria-hidden="true" />{r.location}
                              </span>
                            ) : null}
                            <span className="inline-flex items-center gap-1">
                              <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />opened {fmtDate(r.created_at)}
                            </span>
                            {r.deadline ? (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" aria-hidden="true" />by {fmtDate(r.deadline)}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-2 text-xs font-medium" style={{ color: GOLD_DEEP }}>
                            {r.candidates_shared
                              ? `${r.candidates_shared} profile${r.candidates_shared > 1 ? 's' : ''} shared · tap to read`
                              : 'No profiles shared yet'}
                          </div>
                        </div>
                      </button>
                      {openReq === r.id && (
                        <div
                          className="border-t px-3 sm:px-4 py-3"
                          style={{ borderColor: HAIRLINE, backgroundColor: CREAM }}
                          data-testid={`cp-req-panel-${r.id}`}
                        >
                          {candBusy === r.id ? (
                            <div className="text-sm text-slate-500 flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" /> Loading profiles…
                            </div>
                          ) : (
                            <CandidateList data={candidates[r.id]} />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="agreements" className="mt-4">
            {agreements.length === 0 ? (
              <Empty
                icon={FileText}
                title="No agreement on file yet"
                body="Once your recruitment agreement with DCS is drawn up, the copy sits here with the date it went out and the date it came back signed — so nobody has to hunt through email for it."
                testid="cp-empty-agreements"
              />
            ) : (
              <div className="space-y-2.5">
                {agreements.map((a) => (
                  <div
                    key={a.id}
                    className={`bg-white border rounded-xl px-3 sm:px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 shadow-sm ${a.is_archived ? 'opacity-60' : ''}`}
                    style={{ borderColor: HAIRLINE }}
                    data-testid={`cp-agreement-${a.id}`}
                  >
                    <span className={`${pill} ${AGREEMENT_STYLE[a.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {AGREEMENT_LABEL[a.status] || a.status}
                    </span>
                    <div className="flex-1 min-w-[12rem] text-xs sm:text-sm text-slate-600">
                      Drawn up {fmtDate(a.created_at)}
                      {a.sent_at ? ` · sent ${fmtDate(a.sent_at)}` : ''}
                      {a.signed_uploaded_at ? ` · signed ${fmtDate(a.signed_uploaded_at)}` : ''}
                    </div>
                    {a.view_url ? (
                      <a
                        className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
                        style={{ color: GOLD_DEEP }}
                        href={`${API_URL}${a.view_url}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`cp-agreement-view-${a.id}`}
                      >
                        Open <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="team" className="mt-4">
            <TeamTab team={team} me={me} reload={load} onError={handleErr} />
          </TabsContent>
        </Tabs>

        <footer className="pt-2 pb-6 text-center text-xs text-slate-400">
          <p>Daruwalla Consultancy Services LLP · 29 Hanuman Building, Behind Old RBI, Fort, Mumbai 400001</p>
          <p className="mt-1">
            <a href="mailto:care@daruwallas.com" className="hover:underline" style={{ color: GOLD_DEEP }}>care@daruwallas.com</a>
            {' · '}daruwallas.com
          </p>
        </footer>
      </main>
    </div>
  );
}

function hideOnError(e) {
  if (e && e.currentTarget) e.currentTarget.style.display = 'none';
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <Card style={{ borderColor: HAIRLINE }} data-testid={testid}>
      <CardContent className="p-3 sm:p-4">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: GOLD }} aria-hidden="true" />
        <div className="text-xl sm:text-2xl font-semibold text-slate-900 mt-1.5 leading-none">{value}</div>
        <div className="text-[11px] sm:text-xs text-slate-500 mt-1 leading-tight">{label}</div>
      </CardContent>
    </Card>
  );
}

function Empty({ icon: Icon = Inbox, title, body, testid }) {
  return (
    <div
      className="bg-white border border-dashed rounded-xl p-6 sm:p-8 text-center"
      style={{ borderColor: HAIRLINE }}
      data-testid={testid}
    >
      <span
        className="w-10 h-10 rounded-full inline-flex items-center justify-center"
        style={{ backgroundColor: '#F8F2E2', color: GOLD_DEEP }}
      >
        <Icon className="w-5 h-5" aria-hidden="true" />
      </span>
      <h2 className="mt-3 text-sm font-semibold text-slate-900">{title}</h2>
      <p className="mt-1.5 text-sm text-slate-500 max-w-md mx-auto leading-relaxed">{body}</p>
    </div>
  );
}

function CandidateList({ data }) {
  if (!data) return null;
  const { candidates = [], share = {} } = data;
  if (candidates.length === 0) {
    return (
      <div className="text-sm text-slate-500 flex items-start gap-2" data-testid="cp-empty-candidates">
        <Users className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD_DEEP }} aria-hidden="true" />
        <span>No candidates shared for this role yet — your recruiter will add them here as soon as we shortlist someone.</span>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {candidates.map((c) => (
        <div
          key={c.id}
          className="bg-white border rounded-xl px-3 py-2.5 shadow-sm"
          style={{ borderColor: HAIRLINE }}
          data-testid={`cp-candidate-${c.id}`}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-slate-900">{c.display_name}</span>
            {c.current_designation && <span className="text-sm text-slate-600">· {c.current_designation}</span>}
            {share.show_employer && c.current_company && <span className="text-sm text-slate-600">@ {c.current_company}</span>}
            <span className={`${pill} ml-auto ${STAGE_STYLE[c.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
              {STAGE_LABEL[c.status] || c.status}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
            {c.total_experience_years != null && <span>{c.total_experience_years} yrs experience</span>}
            {c.location && <span>· {c.location}</span>}
            {c.notice_period_days != null && (
              <span>· notice {c.notice_period_days === 0 ? 'immediate' : `${c.notice_period_days} days`}</span>
            )}
            {share.show_ctc && c.expected_ctc != null && <span>· expected {c.expected_ctc} LPA</span>}
            {share.show_contact && c.email && <span>· {c.email}</span>}
            {share.show_contact && c.phone && <span>· {c.phone}</span>}
          </div>
          {c.summary && <p className="text-sm text-slate-700 mt-2 leading-relaxed">{c.summary}</p>}
          {c.skills && c.skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {c.skills.slice(0, 12).map((s) => (
                <Badge key={s} variant="outline" className="text-[10px] font-normal">{s}</Badge>
              ))}
            </div>
          )}
        </div>
      ))}
      {!share.show_contact && (
        <p className="text-xs text-slate-400 pt-1">
          Phone and email stay with us until you tell your recruiter you want to meet the candidate.
        </p>
      )}
    </div>
  );
}

function TeamTab({ team, me, reload, onError }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const isOwner = !!(team && team.is_owner);
  const users = (team && Array.isArray(team.users)) ? team.users : [];

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await portalFetch('/team/invite', { method: 'POST', body: { ...form, portal_role: 'member' } });
      toast.success(res.email_sent ? `Invite emailed to ${res.email}` : `User created — the invite email did not go out (${res.email})`);
      setInviteOpen(false);
      setForm({ email: '', full_name: '', phone: '' });
      reload();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const act = async (path, okMsg) => {
    try {
      await portalFetch(path, { method: 'POST', body: {} });
      toast.success(okMsg);
      reload();
    } catch (err) {
      onError(err);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <p className="text-xs text-slate-500 flex-1">
          {isOwner
            ? 'Anyone you add here gets their own sign-in and sees the same roles and profiles you do.'
            : 'These are the people from your company who can sign in. Ask your portal owner to add anyone else.'}
        </p>
        {isOwner && (
          <Button size="sm" onClick={() => setInviteOpen(true)} className="sm:shrink-0" data-testid="cp-invite-open">
            <UserPlus className="w-4 h-4 mr-1" /> Add a colleague
          </Button>
        )}
      </div>

      {users.length === 0 ? (
        <Empty
          icon={Users}
          title="Nobody listed yet"
          body="Your sign-ins will show here. If this looks wrong, write to care@daruwallas.com and we will check it."
          testid="cp-empty-team"
        />
      ) : users.map((u) => (
        <div
          key={u.id}
          className={`bg-white border rounded-xl px-3 sm:px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 shadow-sm ${u.disabled_at ? 'opacity-60' : ''}`}
          style={{ borderColor: HAIRLINE }}
          data-testid={`cp-team-${u.id}`}
        >
          <div className="flex-1 min-w-[11rem]">
            <div className="font-medium text-slate-900 truncate">
              {u.full_name} {u.id === me?.user?.id && <span className="text-xs text-slate-400">(you)</span>}
            </div>
            <div className="text-xs text-slate-500 truncate">
              <Mail className="w-3 h-3 inline-block mr-1 -mt-px" aria-hidden="true" />
              {u.email}{u.phone ? ` · ${u.phone}` : ''}
              {u.last_login_at ? ` · last sign-in ${fmtDate(u.last_login_at)}` : ' · never signed in'}
            </div>
          </div>
          <Badge variant="outline" className="capitalize">{u.portal_role}</Badge>
          {u.disabled_at && <span className={`${pill} bg-slate-100 text-slate-600 border-slate-200`}>no access</span>}
          {u.must_change_password && !u.disabled_at && (
            <span className={`${pill} bg-amber-50 text-amber-800 border-amber-200`}>invite pending</span>
          )}
          {isOwner && u.id !== me?.user?.id && (
            <div className="flex gap-1">
              {!u.disabled_at && (
                <Button
                  variant="ghost" size="icon" title="Resend invite (new temporary password)"
                  onClick={() => act(`/team/${u.id}/resend-invite`, 'Invite re-sent')}
                  data-testid={`cp-team-resend-${u.id}`}
                >
                  <Send className="w-4 h-4" />
                </Button>
              )}
              {u.disabled_at ? (
                <Button
                  variant="ghost" size="icon" title="Give access back"
                  onClick={() => portalFetch(`/team/${u.id}/disable`, { method: 'POST', body: { disabled: false } })
                    .then(() => { toast.success('Access restored'); reload(); }).catch(onError)}
                  data-testid={`cp-team-enable-${u.id}`}
                >
                  <Power className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost" size="icon" title="Remove access"
                  onClick={() => act(`/team/${u.id}/disable`, 'Access removed')}
                  data-testid={`cp-team-disable-${u.id}`}
                >
                  <PowerOff className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      ))}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="cp-invite-dialog">
          <DialogHeader>
            <DialogTitle>Add a colleague</DialogTitle>
            <DialogDescription>
              They get an email with a temporary password and set their own the first time they sign in.
              They will see the same roles and shared profiles that you do.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={invite} className="space-y-3">
            <div>
              <Label htmlFor="ti-name">Full name</Label>
              <Input id="ti-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="cp-invite-name" />
            </div>
            <div>
              <Label htmlFor="ti-email">Work email</Label>
              <Input id="ti-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="cp-invite-email" />
            </div>
            <div>
              <Label htmlFor="ti-phone">Phone (optional)</Label>
              <Input id="ti-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cp-invite-phone" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="cp-invite-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
